import type { PbjCategoryScore } from "../../../utils/models";
import { computeCumulativePass, DAYS_PER_WEEK_FULL, DAYS_PER_WEEK_WEEKEND } from "./cumulativeScoring.utils";
import type { PlanItem } from "./recommendations.constants";
import { RECOMMENDATION_TYPES } from "./recommendations.constants";

// Recommendation ids that only land on weekend days. Each item's `spend` is
// per-applicable-PPD ($/weekend-PPD for weekend recs, $/calendar-PPD otherwise), so its
// quarter-total dollar cost multiplies by the matching day count. Shared with ActionItems.
export const WEEKEND_REC_IDS = new Set(RECOMMENDATION_TYPES.filter((r) => r.isWeekend).map((r) => r.id));

// Every input the plan-outcome math needs — the same values ActionItems already receives.
export type PlanOutcomeInput = {
	selectedPlan: PlanItem[];
	// HPPD categories (band ladders + rawValue baselines) the cumulative pass scores against.
	categories: PbjCategoryScore[];
	// Total points per raw measure point (`planPointsScale`) — the CMS rescale when a measure is N/A.
	pointsScale: number;
	currentPoints: number;
	// Buffered target points (target × (1 + buffer%)); compared raw so the FE never disagrees
	// with the BE's `targetMet`.
	targetPoints: number;
	census: number;
	quarterDays: number;
	quarterWeekendDays: number;
	// Remaining calendar days (0 when the quarter is past). Switches the displayed PPD onto a
	// catch-up basis in "qtd" mode. The dollar cost is mode-invariant.
	daysRemaining: number;
	periodMode: "qtd" | "quarter";
};

// The single lifted plan outcome — computed once in ScoringAndRecommendations and shared by
// the hero (verdict + gauge secondary arc) and ActionItems (summary). Every points figure
// comes from the cumulative pass so per-item, per-category, and total never disagree.
export type PlanOutcome = {
	// True when the plan has at least one action (a non-empty Action Plan).
	hasPlan: boolean;
	// Signed total marginal points the plan changes (cumulative pass — order/split
	// invariant). Negative when its cuts give up more points than its adds earn.
	pointsGained: number;
	// current + pointsGained, as a raw float (round only for display).
	projectedPoints: number;
	// Points still short of target after the plan (0 when the target is reached), ceil-rounded.
	remainingPoints: number;
	// projectedPoints >= targetPoints (compared raw, matching the BE `targetMet`).
	reachesTarget: boolean;
	// NET PPD across the plan (cost minus savings), on the current period mode's effective
	// window. Negative when the plan saves more than it spends.
	totalPpd: number;
	// NET projected dollars: `totalCost − totalSavings` (mode-invariant: same change = same
	// dollars). Negative when the plan saves more than it spends.
	totalPrice: number;
	// Full projected cost of the plan's ADD items (Σ positive item prices), ≥ 0.
	totalCost: number;
	// Full projected savings of the plan's CUT items (Σ |negative item prices|), ≥ 0. Kept
	// apart from `totalCost` so a mixed plan shows both sides instead of a netted figure that
	// hides how much it adds.
	totalSavings: number;
	// Weekly hours the plan ADDS (magnitude, ≥ 0) — the hero's "+{hours} hrs/wk". Each add
	// item's per-applicable-day rate × days/week (weekend items × 2, else × 7).
	addedWeeklyHours: number;
	// Weekly hours the plan CUTS (magnitude, ≥ 0) — the hero's "−{hours} hrs/wk". Split from
	// `addedWeeklyHours` because a swap (cut RN, add CNA) nets to ~0 and would read "+0 hrs/wk".
	cutWeeklyHours: number;
};

// Per-item SIGNED projected dollars — `item.pricedSpend × census × applicableQuarterDays`; negative
// for a cut (savings), priced from the whole hours the cut commits (see recMath `pricedSpendFor`). Weekend items use the weekend-day count because their hours only land
// on weekend shifts. Mirrors
// ActionItems' `itemPrice` exactly so the hero verdict and the plan summary agree to the cent.
const itemPrice = (item: PlanItem, input: PlanOutcomeInput): number => {
	const days = WEEKEND_REC_IDS.has(item.id) ? input.quarterWeekendDays : input.quarterDays;
	return item.pricedSpend * input.census * days;
};

// Per-item hours per week (per-applicable-day rate × days/week: weekend items × 2, else × 7),
// signed like the item's `hours`.
const itemWeeklyHours = (item: PlanItem): number => item.hours * (WEEKEND_REC_IDS.has(item.id) ? DAYS_PER_WEEK_WEEKEND : DAYS_PER_WEEK_FULL);

// Sum of the positive values and the magnitude of the negative ones, kept apart.
const splitBySign = (values: number[]): { positive: number; negative: number } => {
	let positive = 0;
	let negative = 0;
	for (const v of values) {
		if (v >= 0) positive += v;
		else negative -= v;
	}
	return { positive, negative };
};

// Two spends are "the same" below half a cent (slider values carry float noise; same rule as the
// tracked-plan comparison).
const SAME_SPEND_EPSILON = 0.005;

// Whether the Action Plan is exactly the backend's suggestion — the same levers at the same spends, in
// any order. Only then may the hero say "Suggested plan saves…": a plan the user dragged, trimmed or
// assembled by hand is their plan, even when it started from the suggestion.
export const planMatchesSuggestion = (
	selectedPlan: ReadonlyArray<Pick<PlanItem, "id" | "spend">>,
	steps: ReadonlyArray<{ actionId: string; spend: number }>,
): boolean => {
	if (selectedPlan.length !== steps.length) return false;
	const spendByAction = new Map(steps.map((s) => [s.actionId, s.spend]));
	return selectedPlan.every((item) => {
		const suggested = spendByAction.get(item.id);
		return suggested !== undefined && Math.abs(suggested - item.spend) < SAME_SPEND_EPSILON;
	});
};

/**
 * Lifts the Action-Plan outcome (projected score, cost, reaches-target, headline hours) out
 * of ActionItems so the hero can render the same numbers. Pure — no hooks, no rounding beyond
 * what's documented per field. Empty plan → `hasPlan:false` with a `projectedPoints === current`
 * baseline so the hero can show a "suggest a plan" prompt without special-casing NaNs.
 */
export const computePlanOutcome = (input: PlanOutcomeInput): PlanOutcome => {
	const { selectedPlan, categories, pointsScale, currentPoints, targetPoints, census, quarterDays, daysRemaining, periodMode } = input;

	const cumulative = computeCumulativePass(selectedPlan, categories, pointsScale);
	const pointsGained = cumulative.total;
	const projectedPoints = currentPoints + pointsGained;
	// Raw-float hit math so the UI never disagrees with the BE's `targetMet`.
	const remainingPoints = Math.max(0, Math.ceil(targetPoints - projectedPoints));
	const reachesTarget = remainingPoints === 0;

	const prices = splitBySign(selectedPlan.map((item) => itemPrice(item, input)));
	const totalPrice = prices.positive - prices.negative;
	// Effective window the displayed PPD averages over: quarter mode = full quarter; qtd mode =
	// remaining catch-up days only (so the per-day rate scales up). Guards a completed quarter.
	const effDays = periodMode === "qtd" && daysRemaining > 0 ? daysRemaining : quarterDays;
	const totalPpd = census > 0 && effDays > 0 ? totalPrice / (census * effDays) : 0;

	const weeklyHours = splitBySign(selectedPlan.map(itemWeeklyHours));

	return {
		hasPlan: selectedPlan.length > 0,
		pointsGained,
		projectedPoints,
		remainingPoints,
		reachesTarget,
		totalPpd,
		totalPrice,
		totalCost: prices.positive,
		totalSavings: prices.negative,
		addedWeeklyHours: weeklyHours.positive,
		cutWeeklyHours: weeklyHours.negative,
	};
};
